import ReactHtmlParser from 'react-html-parser';
import {formatHtmlInput, markLinks} from './various';
import sanitizeHtml from 'sanitize-html';
import uniqid from 'uniqid';
import {truncate, words} from 'lodash';

const parserTypes = ['text/html', 'text/plain'];
const NO_PREVIEW_AVAILABLE = 'No preview available. Click here to check the content.';
const NO_CONTENT_AVAILABLE = 'No content available.';

function b64DecodeUnicode(str:string) {
  return decodeURIComponent(Array.prototype.map.call(atob(str), function(c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
  }).join(''))
}

const decodePart = (part:any) => {
  if (parserTypes.indexOf(part.mimeType) > -1 && part.body.data) {
    return b64DecodeUnicode(part.body.data.replace(/-/g, '+').replace(/_/g, '/'));
  }
  return '';
}

const parsePayload = (payload:any) => {
  const { parts } = payload;
  if (parts && parts.length) {
    if (payload.mimeType === 'multipart/alternative') {
      let toDecode = parts.find((p:any) => p.mimeType === 'text/html');
      if (!toDecode) {
        toDecode = parts[0];
      }
      return decodePart(toDecode);
    }
    return parts.map((p:any) => {
      return parsePayload(p);
    }).join('');
  }
  return decodePart(payload);
}

export const extractFiles = (payload:any) => {
  const files:any[] = [];
  const getFileData = (data:any) => {
    if (data.parts && data.parts.length) {
      data.parts.forEach((part:any) => {
        const fileData:any = getFileData(part);
        if (fileData) {
          files.push(fileData);
        }
      })
    }
    if (data.filename) {
      let isInline = false;
      let isAttachment = true;
      let imgId = null;
      const dispositionHeader = data.headers.find((h:any) => h.name === 'Content-Disposition');
      if (dispositionHeader) {
        isAttachment = dispositionHeader.value.indexOf('attachment') > -1;
        isInline = dispositionHeader.value.indexOf('inline') > -1;
      }
      if (isInline) {
        let attIdHeader = data.headers.find((h: any) => h.name === 'X-Attachment-Id');
        if (attIdHeader) {
          imgId = attIdHeader.value;
        } else {
          attIdHeader = data.headers.find((h: any) => h.name === 'Content-Id' || h.name === 'Content-ID');
          if (attIdHeader) {
            imgId = attIdHeader.value.replace(/<|>/g, '');
          }
        }
      }
      files.push({
        headers: data.headers,
        isAttachment,
        isInline,
        imgId: imgId || uniqid(),
        filename: data.filename,
        content: data.body.data,
        mimeType: data.mimeType,
      })
    }
  }
  getFileData(payload);
  return files.flat();
}

export const addInlineFiles = (html:string, files:any[]) => {
  const getImgRx = (cid:string) => {
    return new RegExp(`src="cid:${cid}"`, 'gim');
  }
  if (!files.length) {
    return html;
  }
  let newHTML = html || '';
  files.forEach(file => {
    if (file.isInline && file.imgId && file.content) {
      newHTML = newHTML.replace(getImgRx(file.imgId),(match:any) => {
        return `src="data:${file.mimeType};base64,${file.content.replace(/-/g, '+').replace(/_/g, '/')}"`
      });
    }
  });
  return newHTML;
}

export const formatGmailResult = (result:any, highlight?: string[], isFullEmail?: boolean) => {
  if (!result.payload) {
    return '';
  }
  let htmlInput = parsePayload(result.payload);
  htmlInput = formatHtmlInput(htmlInput, highlight);
  const files = extractFiles(result.payload);
  htmlInput = addInlineFiles(htmlInput, files);
  //@ts-ignore
  return ReactHtmlParser(
    sanitizeHtml(htmlInput, {
      allowedTags: false,
      allowedAttributes: false,
      disallowedTagsMode: 'recursiveEscape',
      allowedSchemesByTag: {
        img: [ 'data', 'http', 'https' ],
      },
    }));
}

export const getGmailAttachments = (result: any) => {
  if (!result.payload) {
    return [];
  }
  return extractFiles(result.payload);
}

export const normalizeGmailResult = (result: any) => {
  const att = getGmailAttachments(result);
  let snip = result.snippet.replace(/\u200C/g, '')
  snip = snip.length > 220 ? truncate(snip, { length: 220 }) : snip + '...';
  if (!snip || !words(snip).length) {
    if (!att.length) {
      snip = NO_PREVIEW_AVAILABLE;
    }
  }
  return {
    ...result,
    title: result.title,
    sender: result.sender ? result.sender.replace(/"/g, '') : '',
    resultSnippet: ReactHtmlParser(markLinks(snip)),
    resultMessage() {
      // on demand parsing of full Email Body
      if (this.fullMessage) {
        return this.fullMessage;
      }
      const message = formatGmailResult(this);
      if (!message || !message.length) {
        return this.fullMessage = NO_CONTENT_AVAILABLE;
      }
      return this.fullMessage = message;
    },
    fullMessage: null,
    pl: 'gmail',
    att,
    date: result.timestamp ? new Date(+result.timestamp).toLocaleString() : '',
  };
}